import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ImagePlus, X } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage, extractFieldErrors } from '@/shared/api/errors';
import { apiSupportStaffPost, apiSupportStaffPut } from '@/entities/support-staff';
import {
  emptySupportStaffFormValues,
  supportStaffSchema,
  type SupportStaffFormValues,
} from '@/features/support-staff/modules/types';
import { FeaturesSupportStaffLanguagesInput } from '@/features/support-staff/modules/ui/LanguagesInput';
import type {
  ApiSupportStaffGetItem,
  ApiSupportStaffPostBody,
} from '@/shared/types/api/support-staff';

type Mode = 'create' | 'edit';

type Props = {
  mode: Mode;
  /** Existing record for `edit` mode. Required when mode === 'edit'. */
  staff?: ApiSupportStaffGetItem;
  /** When in edit mode and the data is still being fetched. */
  loading?: boolean;
  /** When in edit mode and the record could not be found. */
  notFound?: boolean;
  /** When in edit mode and the load failed for another reason. */
  loadError?: string;
  onRetry?: () => void;
};

export const WidgetsSupportStaffFormMain = ({
  mode,
  staff,
  loading,
  notFound,
  loadError,
  onRetry,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const form = useForm<SupportStaffFormValues>({
    resolver: zodResolver(supportStaffSchema),
    defaultValues: emptySupportStaffFormValues,
  });
  const { reset } = form;

  // Hydrate edit form once the record loads.
  useEffect(() => {
    if (mode === 'edit' && staff) {
      reset({
        name: staff.name,
        contact_link: staff.contact_link,
        languages: staff.languages,
        experience_years:
          staff.experience_years !== null && staff.experience_years !== undefined
            ? String(staff.experience_years)
            : '',
        image: undefined,
      });
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [mode, staff, reset]);

  const clearImage = (): void => {
    form.setValue('image', undefined, { shouldValidate: true });
    if (inputRef.current) inputRef.current.value = '';
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const applyServerFieldErrors = (err: unknown): void => {
    const fieldErrors = extractFieldErrors(err);
    Object.entries(fieldErrors).forEach(([field, msg]) => {
      const key = field.replace(/\[\]$/, '').split('.')[0];
      if (key && key in emptySupportStaffFormValues) {
        form.setError(key as keyof SupportStaffFormValues, { type: 'server', message: msg });
      }
    });
  };

  const toBody = (v: SupportStaffFormValues): ApiSupportStaffPostBody => ({
    name: v.name,
    contact_link: v.contact_link,
    languages: v.languages,
    experience_years: v.experience_years.trim() === '' ? null : Number(v.experience_years),
    image: v.image,
  });

  const create = useMutate({
    api: (v: SupportStaffFormValues) => apiSupportStaffPost(toBody(v)),
    invalidateKeys: [queryKeys.supportStaff.all],
    onSuccess: () => {
      toast.success(t('supportStaff.toast.created'));
      navigate('/support-staff');
    },
    onError: (err, message) => {
      applyServerFieldErrors(err);
      toast.error(message);
    },
  });

  const update = useMutate({
    api: (v: SupportStaffFormValues) => apiSupportStaffPut(staff?.id ?? '', toBody(v)),
    invalidateKeys: [queryKeys.supportStaff.all],
    onSuccess: () => {
      toast.success(t('supportStaff.toast.updated'));
      navigate('/support-staff');
    },
    onError: (err, message) => {
      applyServerFieldErrors(err);
      toast.error(message);
    },
  });

  const onSubmit = (v: SupportStaffFormValues): void => {
    if (mode === 'create') {
      create.mutate(v);
      return;
    }
    if (!staff) return;
    update.mutate(v);
  };

  const title =
    mode === 'create' ? t('supportStaff.form.createTitle') : t('supportStaff.form.editTitle');

  // Loading skeleton in edit mode while data is being fetched.
  if (mode === 'edit' && loading) {
    return (
      <>
        <PageHeader title={title} showBack backTo="/support-staff" />
        <div className="flex min-h-[50vh] items-center justify-center">
          <Spinner size="lg" />
        </div>
      </>
    );
  }

  // Not found / load error in edit mode.
  if (mode === 'edit' && (notFound || (!staff && !loading))) {
    return (
      <>
        <PageHeader title={title} showBack backTo="/support-staff" />
        <div className="px-4 py-4 lg:px-6 lg:py-6">
          <ErrorState
            message={loadError ?? t('supportStaff.form.notFound')}
            onRetry={onRetry}
          />
        </div>
      </>
    );
  }

  const submitting = create.isPending || update.isPending;
  const nameError = form.formState.errors.name?.message;
  const contactError = form.formState.errors.contact_link?.message;
  const languagesError = form.formState.errors.languages?.message;
  const experienceError = form.formState.errors.experience_years?.message;
  const imageError = form.formState.errors.image?.message;
  const imageFile = form.watch('image');
  const existingImage = staff?.image ?? null;

  return (
    <>
      <PageHeader title={title} showBack backTo="/support-staff" />
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 px-4 py-4 lg:px-6 lg:py-6"
        data-cy={mode === 'create' ? 'support-staff-create-form' : 'support-staff-edit-form'}
      >
        <div className="flex flex-col gap-3">
          <Input
            label={t('supportStaff.form.fields.name')}
            required
            autoFocus
            error={nameError ? t(nameError) : undefined}
            {...form.register('name')}
          />
          <Input
            label={t('supportStaff.form.fields.contact')}
            required
            placeholder="https://t.me/username"
            error={contactError ? t(contactError) : undefined}
            {...form.register('contact_link')}
          />
          <Controller
            control={form.control}
            name="languages"
            render={({ field }) => (
              <FeaturesSupportStaffLanguagesInput
                value={field.value}
                onChange={field.onChange}
                error={languagesError ? t(languagesError) : undefined}
                label={t('supportStaff.form.fields.languages')}
                placeholder={t('supportStaff.form.fields.languagesPlaceholder')}
                addLabel={t('supportStaff.form.fields.addLanguage')}
              />
            )}
          />
          <Input
            label={t('supportStaff.form.fields.experience')}
            type="number"
            min={0}
            error={experienceError ? t(experienceError) : undefined}
            {...form.register('experience_years')}
          />

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink-soft">
              {t('supportStaff.form.fields.image')}
            </span>
            <Controller
              control={form.control}
              name="image"
              render={({ field }) => (
                <div className="flex flex-col gap-3">
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const picked = e.target.files?.[0];
                      field.onChange(picked ?? undefined);
                      setPreview((prev) => {
                        if (prev) URL.revokeObjectURL(prev);
                        return picked ? URL.createObjectURL(picked) : null;
                      });
                    }}
                  />
                  {imageFile && preview ? (
                    <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <img
                        src={preview}
                        alt=""
                        className="h-20 w-20 shrink-0 rounded-lg object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{imageFile.name}</p>
                        <p className="text-xs text-ink-muted">
                          {(imageFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                      <IconButton label={t('common.delete')} variant="ghost" onClick={clearImage}>
                        <X className="h-4 w-4" />
                      </IconButton>
                    </div>
                  ) : existingImage ? (
                    <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <img
                        src={existingImage}
                        alt=""
                        className="h-20 w-20 shrink-0 rounded-lg object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <Button
                          type="button"
                          variant="outline"
                          leftIcon={<ImagePlus className="h-4 w-4" />}
                          onClick={() => inputRef.current?.click()}
                        >
                          {t('supportStaff.form.fields.pickImage')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      leftIcon={<ImagePlus className="h-4 w-4" />}
                      onClick={() => inputRef.current?.click()}
                    >
                      {t('supportStaff.form.fields.pickImage')}
                    </Button>
                  )}
                  {imageError && (
                    <p className="text-xs font-medium text-red-600">{t(imageError)}</p>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
          <div className="mx-auto flex max-w-3xl gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/support-staff')}
              disabled={submitting}
              block
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting} block>
              {t('common.save')}
            </Button>
          </div>
          {(create.error || update.error) && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {extractErrorMessage(create.error ?? update.error)}
            </p>
          )}
        </div>
      </form>
    </>
  );
};
